<template>
  <div class="admin-sidebar" :class="{ collapsed }">
    <div class="sidebar-content">
      <div class="menu-list">
        <div
          v-for="item in visibleMenuItems"
          :key="item.name"
          class="menu-item"
          :class="{ active: selectedMenu === item.name }"
          @click="handleMenuSelect(item.name)"
        >
          <var-icon :name="item.icon" size="20" />
          <span v-if="!collapsed" class="menu-label">{{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAdminStore } from '@/stores/AdminStore'

defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])
const router = useRouter()
const route = useRoute()
const adminStore = useAdminStore()

const selectedMenu = ref(route.name || 'admin-dashboard')

const menuItems = [
  {
    name: 'admin-dashboard',
    label: '仪表盘',
    icon: 'view-dashboard',
    path: '/admin/dashboard',
    permission: 'dashboard',
  },
  {
    name: 'admin-users',
    label: '用户管理',
    icon: 'account-group',
    path: '/admin/users',
    permission: 'users',
  },
  {
    name: 'admin-sign-statistics',
    label: '签到统计',
    icon: 'format-list-checkbox',
    path: '/admin/sign-statistics',
    permission: 'sign-statistics',
  },
  {
    name: 'admin-logs',
    label: '日志管理',
    icon: 'file-document',
    path: '/admin/logs',
    permission: 'logs',
  },
  {
    name: 'admin-announcements',
    label: '公告中心',
    icon: 'notifications',
    path: '/admin/announcements',
    permission: 'announcements',
  },
  {
    name: 'admin-config',
    label: '配置管理',
    icon: 'cog',
    path: '/admin/config',
    permission: 'config',
  },
  {
    name: 'admin-watch-keys',
    label: '卡密配置',
    icon: 'key-variant',
    path: '/admin/watch/keys',
    permission: 'watch-keys',
  },
  {
    name: 'admin-agents',
    label: '代理管理',
    icon: 'account-multiple',
    path: '/admin/agents',
    permission: 'agents',
  },
  {
    name: 'admin-abuse',
    label: '风控中心',
    icon: 'shield-alert',
    path: '/admin/abuse',
    permission: 'abuse',
  },
]

const visibleMenuItems = computed(() =>
  menuItems.filter((item) => adminStore.hasPermission(item.permission))
)

const handleMenuSelect = (name) => {
  selectedMenu.value = name
  const item = menuItems.find((menuItem) => menuItem.name === name)
  if (!item) return
  router.push({ path: item.path })
  if (window.innerWidth <= 768) {
    emit('close')
  }
}

watch(
  () => route.name,
  (newName) => {
    if (newName === 'admin-user-detail') {
      selectedMenu.value = 'admin-users'
      return
    }
    if (newName) {
      selectedMenu.value = newName
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.admin-sidebar {
  width: 240px;
  height: calc(100vh - 64px);
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(10px);
  border-right: 1px solid var(--border-color);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: fixed;
  left: 0;
  top: 64px;
  z-index: 1000;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

.admin-sidebar.collapsed {
  width: 64px;
}

.sidebar-content {
  padding: 16px 0;
  height: 100%;
  overflow-y: auto;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  margin: 0 8px;
}

.menu-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 0;
  background: var(--primary-color, #0ea5e9);
  border-radius: 0 2px 2px 0;
  transition: height 0.3s ease;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.95);
  transform: translateX(2px);
}

.menu-item.active {
  color: #fff;
  background: rgba(14, 165, 233, 0.15);
  font-weight: 500;
}

.menu-item.active::before {
  height: 60%;
}

.menu-label {
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .admin-sidebar {
    position: fixed;
    left: 0;
    top: 64px;
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.4);
    width: 260px;
  }

  .admin-sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .admin-sidebar.collapsed {
    transform: translateX(-100%);
  }
}
</style>
